import { useUpdateStore, type UpdateController } from '@slate/ui';

/* Tauri-side adapter for the in-app updater. The store holds all UI state
   and the state machine; this module is the only thing that talks to
   `@tauri-apps/plugin-updater` / `@tauri-apps/plugin-process`, so the
   `@slate/ui` package stays web-portable.

   The endpoint is baked into the installer at build time (see
   tauri.conf.json#plugins.updater.endpoints), and CI rewrites it per
   channel. There is no runtime channel switch. */

const STARTUP_DELAY_MS = 4000;

function isTauri(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

const controller: UpdateController = {
  async check() {
    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check();
    if (!update) return null;
    return { version: update.version };
  },
  async install() {
    // Re-query so a Settings re-check between toast push and click can't
    // leave us holding a stale handle.
    const [{ check }, { relaunch }] = await Promise.all([
      import('@tauri-apps/plugin-updater'),
      import('@tauri-apps/plugin-process'),
    ]);
    const update = await check();
    if (!update) throw new Error('No update is available.');
    await update.downloadAndInstall();
    await relaunch(); // never returns
  },
};

/** Boot entrypoint, called once from `main.tsx`. No-op on web. The
 *  `App` component owns the `loadCurrentVersion` effect at mount, so we
 *  don't duplicate it here. By the time the 4 s probe fires, the App
 *  effect has long since populated `currentVersion`. */
export function bootUpdater(): void {
  if (!isTauri()) return;
  // The updater hits the release CDN; in `tauri dev` / `make app run` there's
  // no published `latest.json` for the in-tree version and the noise drowns
  // useful logs. The controller is still attached so the Settings panel's
  // manual "check now" works if the user wants to test it.
  useUpdateStore.getState().attachController(controller);
  if (import.meta.env.DEV) return;
  setTimeout(() => {
    void useUpdateStore.getState().checkNow({ mode: 'startup' });
  }, STARTUP_DELAY_MS);
}
